import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import { FileText, Download, Loader2, FileOutput, Check, Sparkles } from 'lucide-react';

/**
 * DocumentGeneratorButton - Generate PDF/DOCX documents from module data
 * 
 * Props:
 * - documentType: 'Notice' | 'Claim' | 'RFI' | 'SI'
 * - projectId: string
 * - entityId: string
 * - entityData: object containing the entity's data
 * - projectData: object containing project info (name, number, etc.)
 * - onGenerated: callback when document is generated
 */
const DocumentGeneratorButton = ({ 
  documentType, 
  projectId, 
  entityId, 
  entityData, 
  projectData,
  onGenerated 
}) => {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [letterheads, setLetterheads] = useState([]);
  const [selectedLetterhead, setSelectedLetterhead] = useState(undefined);
  const [includeToc, setIncludeToc] = useState(true);
  const [generatePdf, setGeneratePdf] = useState(true);
  const [generateDocx, setGenerateDocx] = useState(true);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [emailCorrespondence, setEmailCorrespondence] = useState([]);
  const [includeEmails, setIncludeEmails] = useState(true);

  useEffect(() => {
    if (open) {
      loadLetterheads();
      loadEmailCorrespondence();
    }
  }, [open]);

  const loadLetterheads = async () => {
    try {
      const response = await api.get('/documents/letterheads');
      setLetterheads(response.data);
      // Auto-select default letterhead
      const defaultLh = response.data.find(lh => lh.is_default);
      if (defaultLh) {
        setSelectedLetterhead(defaultLh.id);
      }
    } catch (error) {
      console.error('Failed to load letterheads');
    }
  };

  const loadEmailCorrespondence = async () => {
    try {
      const response = await api.get('/correspondence/emails', {
        params: {
          project_id: projectId,
          module_type: documentType,
          module_entity_id: entityId
        }
      });
      setEmailCorrespondence(response.data || []);
    } catch (error) {
      console.error('Failed to load email correspondence');
      setEmailCorrespondence([]);
    }
  };

  // Helper function to convert content (plain text, markdown, or HTML) to content blocks
  // User preference: Convert numbered lists to bullet points
  // Improved: Better paragraph separation and heading detection
  const parseContentToBlocks = (content, startOrder) => {
    const blocks = [];
    let order = startOrder;
    
    if (!content) return { blocks, order };
    
    // Check if content is HTML (from rich text editor)
    const isHTML = /<[^>]+>/.test(content);
    
    if (isHTML) {
      // Parse HTML content
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');
      
      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            // Split text by newlines to create separate paragraphs
            const lines = text.split(/\n+/).filter(l => l.trim());
            for (const line of lines) {
              const trimmedLine = line.trim();
              if (trimmedLine) {
                // Check if it's an uppercase heading
                if (/^[A-Z][A-Z\s\d.()]+$/.test(trimmedLine) && trimmedLine.length < 80) {
                  blocks.push({
                    id: `html_h_${order}`,
                    type: 'heading',
                    heading_level: 2,
                    heading_text: trimmedLine,
                    order: order++
                  });
                } else {
                  blocks.push({
                    id: `html_p_${order}`,
                    type: 'paragraph',
                    text: trimmedLine,
                    order: order++
                  });
                }
              }
            }
          }
          return;
        }
        
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        
        const tagName = node.tagName.toLowerCase();
        
        // Handle headings
        if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
          const level = parseInt(tagName[1]);
          blocks.push({
            id: `html_h_${order}`,
            type: 'heading',
            heading_level: Math.min(level, 3),
            heading_text: node.textContent.trim(),
            order: order++
          });
          return;
        }
        
        // Handle lists - ALL lists become bullet points (user requirement)
        if (tagName === 'ul' || tagName === 'ol') {
          const items = Array.from(node.querySelectorAll('li'))
            .map(li => li.textContent.trim())
            .filter(text => text);
          if (items.length > 0) {
            blocks.push({
              id: `html_list_${order}`,
              type: 'bullet_list',  // Always use bullet_list (user requirement)
              items: items,
              order: order++
            });
          }
          return;
        }
        
        // Handle paragraphs
        if (tagName === 'p') {
          const text = node.textContent.trim();
          if (text) {
            blocks.push({
              id: `html_p_${order}`,
              type: 'paragraph',
              text: text,
              order: order++
            });
          }
          return;
        }
        
        // Handle divs and other containers
        if (['div', 'section', 'article', 'span'].includes(tagName)) {
          for (const child of node.childNodes) {
            processNode(child);
          }
          return;
        }
        
        // Handle line breaks - create new paragraph
        if (tagName === 'br') {
          return;
        }
        
        // Default: get text content
        const text = node.textContent.trim();
        if (text) {
          blocks.push({
            id: `html_default_${order}`,
            type: 'paragraph',
            text: text,
            order: order++
          });
        }
      };
      
      for (const child of doc.body.childNodes) {
        processNode(child);
      }
    } else {
      // Parse plain text content - IMPROVED to split by single newlines
      // First, split by lines to properly identify sections
      const lines = content.split(/\n/).map(l => l.trim()).filter(l => l);
      
      let currentParagraph = [];
      
      const flushParagraph = () => {
        if (currentParagraph.length > 0) {
          const text = currentParagraph.join(' ').trim();
          if (text) {
            blocks.push({
              id: `text_p_${order}`,
              type: 'paragraph',
              text: text,
              order: order++
            });
          }
          currentParagraph = [];
        }
      };
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        // Check for section headers (ALL UPPERCASE text on its own line)
        // e.g., "CONTRACTOR DETAILS", "NOTIFICATION (CLAUSE 23.4.2)", "DELAY EVENT DESCRIPTION"
        const isUppercaseHeading = /^[A-Z][A-Z\s\d.(),:-]+$/.test(line) && 
                                   line.length < 80 && 
                                   !line.includes('@') && // Not an email
                                   !line.startsWith('Tel:') &&
                                   !line.startsWith('Email:') &&
                                   !line.startsWith('Address:') &&
                                   !line.startsWith('Company:');
        
        if (isUppercaseHeading) {
          flushParagraph();
          blocks.push({
            id: `text_h_${order}`,
            type: 'heading',
            heading_level: 2,
            heading_text: line,
            order: order++
          });
          continue;
        }
        
        // Check for numbered section headers (e.g., "1. NOTIFICATION")
        const numberedHeadingMatch = line.match(/^(\d+\.?\s*)([A-Z][A-Z\s\d.(),:-]+)$/);
        if (numberedHeadingMatch && line.length < 80) {
          flushParagraph();
          blocks.push({
            id: `text_h_${order}`,
            type: 'heading',
            heading_level: 2,
            heading_text: line,
            order: order++
          });
          continue;
        }
        
        // Check for markdown-style headings (## Heading)
        const markdownHeadingMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (markdownHeadingMatch) {
          flushParagraph();
          const level = markdownHeadingMatch[1].length;
          blocks.push({
            id: `md_h_${order}`,
            type: 'heading',
            heading_level: Math.min(level, 3),
            heading_text: markdownHeadingMatch[2].replace(/\*\*/g, '').trim(),
            order: order++
          });
          continue;
        }
        
        // Check for field labels that should be on their own line
        // e.g., "Company: Gantt PM Software", "Address: 20 Canton Park"
        const fieldMatch = line.match(/^(Company|Address|Tel|Email|Contact|Signatory|Project|Contract|Date|Reference):\s*(.+)$/i);
        if (fieldMatch) {
          flushParagraph();
          blocks.push({
            id: `text_field_${order}`,
            type: 'paragraph',
            text: line,
            order: order++
          });
          continue;
        }
        
        // Check if it's a bullet list item (- or * or • prefix)
        if (/^[-*•]\s+/.test(line)) {
          flushParagraph();
          // Collect consecutive bullet items
          const bulletItems = [line.replace(/^[-*•]\s+/, '').trim()];
          while (i + 1 < lines.length && /^[-*•]\s+/.test(lines[i + 1])) {
            i++;
            bulletItems.push(lines[i].replace(/^[-*•]\s+/, '').trim());
          }
          blocks.push({
            id: `text_bullet_${order}`,
            type: 'bullet_list',
            items: bulletItems,
            order: order++
          });
          continue;
        }
        
        // Check if it's a numbered list item (1., 2., etc.)
        // Convert to BULLET LIST per user requirement
        if (/^\d+[.)]\s+/.test(line)) {
          flushParagraph();
          // Collect consecutive numbered items
          const numberedItems = [line.replace(/^\d+[.)]\s+/, '').trim()];
          while (i + 1 < lines.length && /^\d+[.)]\s+/.test(lines[i + 1])) {
            i++;
            numberedItems.push(lines[i].replace(/^\d+[.)]\s+/, '').trim());
          }
          blocks.push({
            id: `text_numlist_${order}`,
            type: 'bullet_list',  // Convert to bullet (user requirement)
            items: numberedItems,
            order: order++
          });
          continue;
        }
        
        // Check if it's a lettered list item (a., b., etc.)
        if (/^[a-z][.)]\s+/i.test(line)) {
          flushParagraph();
          // Collect consecutive lettered items
          const letteredItems = [line.replace(/^[a-z][.)]\s+/i, '').trim()];
          while (i + 1 < lines.length && /^[a-z][.)]\s+/i.test(lines[i + 1])) {
            i++;
            letteredItems.push(lines[i].replace(/^[a-z][.)]\s+/i, '').trim());
          }
          blocks.push({
            id: `text_letterlist_${order}`,
            type: 'bullet_list',  // Convert to bullet (user requirement)
            items: letteredItems,
            order: order++
          });
          continue;
        }
        
        // Check if there's an empty line in the original content (paragraph break)
        // Look at original content to see if this line was followed by empty line
        const originalIndex = content.indexOf(line);
        const afterLine = content.substring(originalIndex + line.length);
        const hasEmptyLineAfter = /^\s*\n\s*\n/.test(afterLine);
        
        // Add to current paragraph
        currentParagraph.push(line);
        
        // If there was an empty line after, flush the paragraph
        if (hasEmptyLineAfter) {
          flushParagraph();
        }
      }
      
      // Flush any remaining paragraph
      flushParagraph();
    }
    
    // Clean up text in all blocks
    for (const block of blocks) {
      if (block.text) {
        block.text = block.text
          .replace(/\*\*([^*]+)\*\*/g, '$1')  // Remove bold markers
          .replace(/\*([^*]+)\*/g, '$1')       // Remove italic markers
          .replace(/`([^`]+)`/g, '$1')         // Remove code markers
          .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // Remove links, keep text
          .trim();
      }
      if (block.items) {
        block.items = block.items.map(item => 
          item
            .replace(/\*\*([^*]+)\*\*/g, '$1')
            .replace(/\*([^*]+)\*/g, '$1')
            .replace(/`([^`]+)`/g, '$1')
            .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
            .trim()
        );
      }
    }
    
    return { blocks, order };
  };

  const buildContentBlocks = () => {
    const blocks = [];
    let order = 1;

    if (documentType === 'Notice') {
      // Notice document structure
      blocks.push({
        id: `h_intro_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '1. Introduction',
        order: order++
      });
      blocks.push({
        id: `p_intro_${order}`,
        type: 'paragraph',
        text: `This Notice is issued in accordance with the provisions of the JBCC Principal Building Agreement (PBA) to formally notify the Principal Agent of a delay event affecting the Works.`,
        order: order++
      });

      blocks.push({
        id: `h_contract_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '2. Contractual Basis',
        order: order++
      });
      const clauseText = entityData.clause_reference || 'JBCC Clause 23.5';
      blocks.push({
        id: `p_contract_${order}`,
        type: 'paragraph',
        text: `This notice is submitted in terms of ${clauseText} of the Contract, which requires the Contractor to notify the Principal Agent of any circumstances which may entitle the Contractor to an extension of the Date for Practical Completion.`,
        order: order++
      });

      blocks.push({
        id: `h_event_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '3. Event Description',
        order: order++
      });
      
      // Parse the edited content to preserve paragraphs and formatting
      const contentText = entityData.edited_content || entityData.generated_content || 'Event description not provided.';
      const { blocks: eventBlocks, order: newOrder } = parseContentToBlocks(contentText, order);
      blocks.push(...eventBlocks);
      order = newOrder;

      blocks.push({
        id: `h_impact_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '4. Impact Assessment',
        order: order++
      });
      const impactType = entityData.impact_type === 'time' ? 'Time Extension' : 
                         entityData.impact_type === 'cost' ? 'Cost Compensation' : 
                         'Time Extension and Cost Compensation';
      blocks.push({
        id: `p_impact_${order}`,
        type: 'paragraph',
        text: `The Contractor seeks ${impactType} as a result of this delay event. A detailed quantum assessment will be provided in the formal Extension of Time Claim to follow.`,
        order: order++
      });

      blocks.push({
        id: `h_reservation_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '5. Reservation of Rights',
        order: order++
      });
      blocks.push({
        id: `p_reservation_${order}`,
        type: 'paragraph',
        text: 'The Contractor reserves all rights under the Contract and at law in respect of this delay event. This notice does not constitute a waiver of any right to claim further extensions of time or additional compensation as may become apparent.',
        order: order++
      });

    } else if (documentType === 'Claim') {
      // Claim document structure
      blocks.push({
        id: `h_exec_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '1. Executive Summary',
        order: order++
      });
      blocks.push({
        id: `p_exec_${order}`,
        type: 'paragraph',
        text: `This Extension of Time (EOT) Claim is submitted in respect of delay events affecting the critical path of the Works. The Contractor seeks an extension of ${entityData.days_claimed || 'X'} calendar days and/or cost compensation of R ${entityData.amount_claimed?.toLocaleString() || 'TBD'}.`,
        order: order++
      });

      blocks.push({
        id: `h_intro_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '2. Introduction',
        order: order++
      });
      blocks.push({
        id: `p_intro_${order}`,
        type: 'paragraph',
        text: `This claim is submitted in accordance with JBCC Clause 23.6, which requires the Contractor to submit a properly substantiated claim within 40 days of the delay event ceasing.`,
        order: order++
      });

      blocks.push({
        id: `h_contract_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '3. Contractual Framework',
        order: order++
      });
      blocks.push({
        id: `p_contract_${order}`,
        type: 'paragraph',
        text: `The Contract between the parties is governed by the JBCC Principal Building Agreement. The relevant clauses for this claim are Clause 23.5 (Notice of Delay) and Clause 23.6 (Extension of Time Claim).`,
        order: order++
      });

      blocks.push({
        id: `h_factual_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '4. Factual Narrative',
        order: order++
      });
      
      // Parse the claim content to preserve paragraphs and formatting
      const claimText = entityData.edited_content || entityData.generated_content || 'Claim narrative not provided.';
      const { blocks: claimBlocks, order: claimOrder } = parseContentToBlocks(claimText, order);
      blocks.push(...claimBlocks);
      order = claimOrder;

      blocks.push({
        id: `h_quantum_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '5. Quantum',
        order: order++
      });
      
      const quantumItems = [];
      if (entityData.days_claimed) {
        quantumItems.push(`Time Entitlement: ${entityData.days_claimed} calendar days`);
      }
      if (entityData.amount_claimed) {
        quantumItems.push(`Cost Entitlement: R ${entityData.amount_claimed.toLocaleString()}`);
      }
      
      if (quantumItems.length > 0) {
        blocks.push({
          id: `list_quantum_${order}`,
          type: 'bullet_list',
          items: quantumItems,
          order: order++
        });
      } else {
        blocks.push({
          id: `p_quantum_${order}`,
          type: 'paragraph',
          text: 'Quantum to be determined.',
          order: order++
        });
      }

      blocks.push({
        id: `h_conclusion_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '6. Conclusion',
        order: order++
      });
      blocks.push({
        id: `p_conclusion_${order}`,
        type: 'paragraph',
        text: 'Based on the foregoing, the Contractor respectfully requests that the Principal Agent assess and approve this claim in accordance with the Contract.',
        order: order++
      });

      blocks.push({
        id: `h_reservation_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '7. Reservation of Rights',
        order: order++
      });
      blocks.push({
        id: `p_reservation_${order}`,
        type: 'paragraph',
        text: 'The Contractor reserves all rights under the Contract and at law. This claim does not constitute a waiver of any right to claim further extensions of time or additional compensation.',
        order: order++
      });

    } else if (documentType === 'RFI') {
      // RFI document structure
      blocks.push({
        id: `h_query_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '1. Request for Information',
        order: order++
      });
      blocks.push({
        id: `p_query_${order}`,
        type: 'paragraph',
        text: entityData.query || entityData.title || 'No query provided.',
        order: order++
      });

      if (entityData.discipline) {
        blocks.push({
          id: `h_discipline_${order}`,
          type: 'heading',
          heading_level: 2,
          heading_text: 'Discipline',
          order: order++
        });
        blocks.push({
          id: `p_discipline_${order}`,
          type: 'paragraph',
          text: entityData.discipline,
          order: order++
        });
      }

      if (entityData.response_text) {
        blocks.push({
          id: `h_response_${order}`,
          type: 'heading',
          heading_level: 1,
          heading_text: '2. Response',
          order: order++
        });
        blocks.push({
          id: `p_response_${order}`,
          type: 'paragraph',
          text: entityData.response_text,
          order: order++
        });
      }

    } else if (documentType === 'SI') {
      // Site Instruction document structure
      blocks.push({
        id: `h_instruction_${order}`,
        type: 'heading',
        heading_level: 1,
        heading_text: '1. Site Instruction',
        order: order++
      });
      blocks.push({
        id: `p_instruction_${order}`,
        type: 'paragraph',
        text: entityData.instruction || entityData.title || 'No instruction provided.',
        order: order++
      });

      if (entityData.reason) {
        blocks.push({
          id: `h_reason_${order}`,
          type: 'heading',
          heading_level: 1,
          heading_text: '2. Reason for Instruction',
          order: order++
        });
        blocks.push({
          id: `p_reason_${order}`,
          type: 'paragraph',
          text: entityData.reason,
          order: order++
        });
      }

      if (entityData.impact_assessment) {
        blocks.push({
          id: `h_impact_${order}`,
          type: 'heading',
          heading_level: 1,
          heading_text: '3. Impact Assessment',
          order: order++
        });
        
        const impactItems = [];
        if (entityData.impact_assessment.time_impact) {
          impactItems.push(`Time Impact: ${entityData.impact_assessment.time_impact}`);
        }
        if (entityData.impact_assessment.cost_impact) {
          impactItems.push(`Cost Impact: ${entityData.impact_assessment.cost_impact}`);
        }
        if (entityData.impact_assessment.scope_impact) {
          impactItems.push(`Scope Impact: ${entityData.impact_assessment.scope_impact}`);
        }
        
        if (impactItems.length > 0) {
          blocks.push({
            id: `list_impact_${order}`,
            type: 'bullet_list',
            items: impactItems,
            order: order++
          });
        }
      }

      if (entityData.contractor_response) {
        blocks.push({
          id: `h_response_${order}`,
          type: 'heading',
          heading_level: 1,
          heading_text: '4. Contractor Response',
          order: order++
        });
        blocks.push({
          id: `p_response_${order}`,
          type: 'paragraph',
          text: entityData.contractor_response,
          order: order++
        });
      }
    }

    return blocks;
  };

  const buildAnnexures = () => {
    const annexures = [];
    let annexureIndex = 0;
    
    // Add file attachments as annexures
    if (entityData.attachments && entityData.attachments.length > 0) {
      entityData.attachments.forEach((att, idx) => {
        annexures.push({
          id: `ann_file_${idx}`,
          label: String.fromCharCode(65 + annexureIndex), // A, B, C...
          title: att.filename || att.name || `Attachment ${idx + 1}`,
          description: att.description || null,
          source_type: att.type === 'program' ? 'Programme' : 
                       att.type === 'cost' ? 'Cost' : 
                       att.type === 'photo' ? 'Photo' : 'Other',
          source_date: att.uploaded_at || new Date().toISOString(),
          file_path: att.path || null,
          file_type: att.file_type || att.type,
          order: annexureIndex++
        });
      });
    }

    // Add email correspondence as annexures (if enabled and available)
    if (includeEmails && emailCorrespondence && emailCorrespondence.length > 0) {
      emailCorrespondence.forEach((email, idx) => {
        annexures.push({
          id: `ann_email_${idx}`,
          label: String.fromCharCode(65 + annexureIndex), // Continue labeling
          title: `Email: ${email.subject || 'No Subject'}`,
          description: `From: ${email.from_email} | To: ${email.to?.join(', ') || 'N/A'} | Date: ${new Date(email.email_date || email.created_at).toLocaleDateString()}`,
          source_type: 'Email',
          source_id: email.id,
          source_date: email.email_date || email.created_at,
          order: annexureIndex++
        });
      });
    }

    return annexures;
  };

  const handleGenerate = async () => {
    if (!generatePdf && !generateDocx) {
      toast.error('Please select at least one output format');
      return;
    }

    setGenerating(true);
    setGeneratedDoc(null);

    try {
      // For RFI documents, use the professional RFI PDF template
      if (documentType === 'RFI' && generatePdf) {
        const response = await api.get(`/projects/${projectId}/rfis/${entityId}/pdf`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const rfiNumber = entityData.rfi_number || `RFI-${entityId.slice(0, 8).toUpperCase()}`;
        link.download = `${rfiNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Professional RFI document downloaded successfully!');
        setOpen(false);
        return;
      }

      // For Notice documents, use the professional Notice PDF template
      if (documentType === 'Notice' && generatePdf) {
        const response = await api.get(`/notices/${entityId}/professional-pdf`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const noticeNumber = entityData.notice_number || `Notice-${entityId.slice(0, 8).toUpperCase()}`;
        link.download = `${noticeNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Professional Notice document downloaded successfully!');
        setOpen(false);
        return;
      }

      // For Claim documents, use the professional Claim PDF template
      if (documentType === 'Claim' && generatePdf) {
        const response = await api.get(`/claims/${entityId}/professional-pdf`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const claimNumber = entityData.claim_number || `Claim-${entityId.slice(0, 8).toUpperCase()}`;
        link.download = `${claimNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Professional Claim document downloaded successfully!');
        setOpen(false);
        return;
      }

      const contentBlocks = buildContentBlocks();
      const annexures = buildAnnexures();
      
      const referenceNumber = entityData.reference_number || 
                              entityData.notice_number || 
                              entityData.claim_number ||
                              `${documentType.toUpperCase()}-${entityId.slice(0, 8)}`;

      const payload = {
        document_type: documentType,
        project_id: projectId,
        entity_id: entityId,
        content_blocks: contentBlocks,
        annexures: annexures,
        metadata: {
          project_name: projectData?.name || 'Unknown Project',
          project_number: projectData?.project_number || null,
          client_name: projectData?.client_name || null,
          document_title: documentType === 'Notice' 
            ? `Notice of Delay - ${entityData.title || referenceNumber}`
            : `Extension of Time Claim - ${entityData.title || referenceNumber}`,
          document_type: documentType,
          reference_number: referenceNumber,
          date: new Date().toISOString(),
          prepared_by: null,
          confidentiality: 'CONFIDENTIAL'
        },
        letterhead_id: selectedLetterhead || null,
        include_toc: includeToc,
        toc_on_separate_page: true,
        generate_pdf: generatePdf,
        generate_docx: generateDocx
      };

      const response = await api.post('/documents/generate', payload);
      console.log('Document generated:', response.data);
      setGeneratedDoc(response.data);
      toast.success('Document generated successfully!');
      
      if (onGenerated) {
        onGenerated(response.data);
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate document: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (format) => {
    if (!generatedDoc) return;
    
    try {
      const response = await api.get(`/documents/generated/${generatedDoc.id}/download/${format}`, {
        responseType: 'blob'
      });
      
      // Check if the response is actually an error JSON (not a blob)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const err = JSON.parse(text);
        toast.error(err.detail || `Failed to download ${format.toUpperCase()}`);
        return;
      }

      const contentDisposition = response.headers?.['content-disposition'];
      let filename = `${generatedDoc.reference_number}.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const detail = error.response?.data;
      let msg = `Failed to download ${format.toUpperCase()}`;
      if (detail instanceof Blob) {
        try {
          const text = await detail.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch {}
      } else if (typeof detail === 'object' && detail?.detail) {
        msg = detail.detail;
      }
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
          size="sm"
          data-testid="generate-document-btn"
        >
          <FileOutput className="h-3.5 w-3.5 sm:mr-1.5" />
          <span className="hidden sm:inline">Generate Document</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-barlow font-bold uppercase">
            Generate {documentType} Document
          </DialogTitle>
          <DialogDescription>
            Create a professional PDF or DOCX document from this {documentType.toLowerCase()}.
          </DialogDescription>
        </DialogHeader>

        {!generatedDoc ? (
          <div className="space-y-4">
            {/* Letterhead Selection */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Letterhead Template
              </Label>
              <Select value={selectedLetterhead || "none"} onValueChange={(val) => setSelectedLetterhead(val === "none" ? undefined : val)}>
                <SelectTrigger className="rounded-sm mt-1">
                  <SelectValue placeholder="Select letterhead (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No letterhead</SelectItem>
                  {letterheads.map((lh) => (
                    <SelectItem key={lh.id} value={lh.id}>
                      {lh.name} {lh.is_default && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {letterheads.length === 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  No letterheads uploaded. Go to Documents → Upload Letterhead.
                </p>
              )}
            </div>

            {/* Output Options */}
            <div className="space-y-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Output Options
              </Label>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="generate-pdf" 
                  checked={generatePdf}
                  onCheckedChange={setGeneratePdf}
                />
                <label htmlFor="generate-pdf" className="text-sm cursor-pointer">
                  Generate PDF
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="generate-docx" 
                  checked={generateDocx}
                  onCheckedChange={setGenerateDocx}
                />
                <label htmlFor="generate-docx" className="text-sm cursor-pointer">
                  Generate DOCX (editable)
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="include-toc" 
                  checked={includeToc}
                  onCheckedChange={setIncludeToc}
                />
                <label htmlFor="include-toc" className="text-sm cursor-pointer">
                  Include Table of Contents
                </label>
              </div>

              {emailCorrespondence.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="include-emails" 
                    checked={includeEmails}
                    onCheckedChange={setIncludeEmails}
                  />
                  <label htmlFor="include-emails" className="text-sm cursor-pointer">
                    Include {emailCorrespondence.length} email(s) as annexures
                  </label>
                </div>
              )}
            </div>

            {/* Preview Info */}
            <div className="bg-slate-50 rounded-sm p-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Document Preview
              </h4>
              <div className="text-sm space-y-1">
                <p><span className="text-slate-500">Type:</span> {documentType}</p>
                <p><span className="text-slate-500">Project:</span> {projectData?.name || 'Unknown'}</p>
                <p><span className="text-slate-500">Reference:</span> {entityData.reference_number || entityData.notice_number || entityData.claim_number || 'Auto-generated'}</p>
                <p>
                  <span className="text-slate-500">Annexures:</span>{' '}
                  {(entityData.attachments?.length || 0) + (includeEmails ? emailCorrespondence.length : 0)} items
                  {entityData.attachments?.length > 0 && ` (${entityData.attachments.length} files)`}
                  {includeEmails && emailCorrespondence.length > 0 && ` (${emailCorrespondence.length} emails)`}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setOpen(false)} 
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleGenerate} 
                disabled={generating}
                className="flex-1 rounded-sm bg-emerald-600 hover:bg-emerald-700"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <Sparkles className="h-3 w-3 mr-1 animate-pulse text-amber-300" />
                    AI Generating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Success State */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-center">
              <Check className="h-12 w-12 mx-auto text-emerald-500 mb-2" />
              <h4 className="font-semibold text-emerald-700">Document Generated!</h4>
              <p className="text-sm text-emerald-600 mt-1">
                {generatedDoc.page_count} pages • {generatedDoc.annexure_count} annexures
              </p>
            </div>

            {/* Download Buttons */}
            <div className="flex gap-3">
              {(generatedDoc.has_pdf || generatedDoc.pdf_path) && (
                <Button 
                  onClick={() => handleDownload('pdf')}
                  className="flex-1 rounded-sm bg-red-600 hover:bg-red-700"
                  data-testid="download-pdf-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              )}
              {(generatedDoc.has_docx || generatedDoc.docx_path) && (
                <Button 
                  onClick={() => handleDownload('docx')}
                  className="flex-1 rounded-sm bg-blue-600 hover:bg-blue-700"
                  data-testid="download-docx-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download DOCX
                </Button>
              )}
            </div>

            {/* Generate Another */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setOpen(false)} 
                className="flex-1 rounded-sm"
              >
                Close
              </Button>
              <Button 
                variant="outline"
                onClick={() => setGeneratedDoc(null)} 
                className="flex-1 rounded-sm"
              >
                Generate Another
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentGeneratorButton;
